import React, { useEffect } from 'react';
import Header from '../components/Header';
import Partners from '../components/Partners';
import Compliance from '../components/Compliance';
import Footer from '../components/Footer';

export default function TermsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF5]">
      <Header />

      {/* Hero */}
      <div style={{ background: '#0B1F2D', paddingBottom: 64 }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 0' }}>
          <div style={{ fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7BC2B0', fontWeight: 500, marginBottom: 12 }}>Legal</div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(36px,5vw,60px)', lineHeight: 1.02, color: '#FAFAF5', letterSpacing: '-0.02em', marginBottom: 16 }}>Terms of Service</h1>
          <p style={{ fontSize: 15, color: 'rgba(250,250,245,0.6)', lineHeight: 1.6 }}>Last updated: May 7, 2026</p>
        </div>
      </div>

      {/* Intro */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 0' }}>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)', padding: '20px 24px', borderRadius: 12, background: '#E8F3EE', borderLeft: '3px solid #1B6F5E' }}>
          These Terms of Service ("Terms") govern access to and use of the ESGine® platform, website and related services ("Services") provided by ESGine® ("ESGine", "we", "us", "our"). By accessing or using the Services, you agree to be bound by these Terms. If you are accepting these Terms on behalf of an organization, you confirm that you are authorized to do so.
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 96px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>

          <Section title="1. Definitions">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Def term='"Client"' def='means the organization that has entered into an agreement or order form with ESGine® for access to the Services.' />
              <Def term='"Authorized User"' def="means an individual who is authorized by the Client to access and use the Services on the Client's behalf." />
              <Def term='"Client Data"' def='means any data, documents, files, ESG/Climate submission packages or other materials uploaded to or processed via the Services by or on behalf of the Client.' />
              <Def term='"Order Form"' def='means any agreement, statement of work, proposal or ordering document executed by ESGine® and the Client describing the Services, scope and commercial terms.' />
            </div>
          </Section>

          <Section title="2. Scope of the Services">
            <P>ESGine® is the world's only ESG-as-Code® compliance platform. The Services evaluate ESG and climate disclosures against specific regulatory rules across seven frameworks and 90+ rules. Every outcome maps to a specific regulatory clause. Outputs generated by the Services are intended to support the Client's own governance, review, and decision-making processes.</P>
          </Section>

          <Section title="3. No legal or assurance advice">
            <P>ESGine® is not a law firm, accounting firm or assurance provider and does not provide legal, regulatory, accounting, auditing or assurance services. Nothing in the Services, Documentation or communications from ESGine® should be construed as legal, regulatory or assurance advice, or as a substitute for the Client obtaining advice from its own professional advisers.</P>
            <P>The Client remains solely responsible for the content, accuracy and completeness of its ESG and climate disclosures, filings, reports and communications, and for determining whether, how and when to submit any materials to regulators, exchanges, investors or other stakeholders.</P>
          </Section>

          <Section title="4. Client responsibilities">
            <P>The Client is responsible for ensuring that it has all necessary rights, permissions and lawful bases to upload Client Data to the Services and to instruct ESGine to process such data; that Client Data does not infringe third-party rights or violate applicable laws; and that Authorized Users comply with these Terms and maintain the confidentiality of their account credentials.</P>
          </Section>

          <Section title="5. Acceptable use">
            <P>The Client and its Authorized Users must not use the Services in any manner that is unlawful, deceptive or misleading; attempt to gain unauthorized access to the Services or related systems; interfere with or disrupt the integrity or performance of the Services; reverse engineer or decompile the Services (except as permitted by law); or use the Services to create a competing product or service.</P>
          </Section>

          <Section title="6. Client Data and confidentiality">
            <P>As between the parties, the Client retains ownership of all rights, title and interest in and to Client Data. ESGine® will process Client Data solely for the purpose of providing the Services and will treat Client Data as confidential information, subject to limited disclosures to personnel and service providers who need access to deliver the Services and who are bound by confidentiality obligations.</P>
          </Section>

          <Section title="7. Intellectual property">
            <P>ESGine® and its licensors retain all rights, title and interest in and to the underlying software, rule libraries, ESG-as-Code®, models, Documentation and related intellectual property. No rights are granted to the Client except as expressly stated in these Terms or in an applicable Order Form.</P>
          </Section>

          <Section title="8. Fees, term and termination">
            <P>Fees, billing terms and subscription or pilot periods will be set out in the applicable Order Form or written agreement between ESGine® and the Client. Either party may terminate the agreement as permitted under that document and applicable law. Upon termination or expiry, the Client's right to access the Services will cease and ESGine® will handle Client Data in accordance with the agreement and applicable law, including any agreed data return or deletion procedures.</P>
          </Section>

          <Section title="9. Warranties and disclaimers">
            <P>ESGine® will provide the Services with reasonable skill and care. Except as expressly stated, the Services are provided "as is" and "as available" and ESGine® disclaims all other warranties, whether express, implied, statutory or otherwise, to the fullest extent permitted by law.</P>
          </Section>

          <Section title="10. Limitation of liability">
            <P>To the fullest extent permitted by law, ESGine® will not be liable for any indirect, consequential, special, incidental or punitive damages, or for loss of profits, revenue, business or data. ESGine's total aggregate liability arising out of or in connection with the Services and these Terms will be limited to the amount of fees actually paid by the Client for the Services giving rise to the claim during the twelve (12) months immediately preceding the event giving rise to such liability, or such other amount as may be specified in the applicable Order Form.</P>
          </Section>

          <Section title="11. Governing law and jurisdiction">
            <P>Unless otherwise agreed in writing in an applicable Order Form or other binding agreement, these Terms and any dispute or claim arising out of or in connection with them will be governed by the laws of the jurisdiction specified in that agreement or, where no such jurisdiction is specified, by the laws of the jurisdiction most closely connected to the relevant ESGine® entity, service engagement or contracting party.</P>
            <P>Depending on the nature of the engagement, this may include the United States or the United Kingdom. The parties agree that disputes may be brought before the courts of the applicable jurisdiction, subject to any mandatory rights or remedies available under applicable law.</P>
          </Section>

          <Section title="12. Changes to these Terms">
            <P>ESGine® may update these Terms from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and may provide additional notice where appropriate. Your continued use of the Services after such changes take effect constitutes acceptance of the updated Terms.</P>
          </Section>

          <Section title="Contact">
            <P>For questions about these Terms, please contact us:</P>
            <div style={{ marginTop: 16, padding: '20px 24px', borderRadius: 12, border: '1px solid #E8E5DC', background: 'white' }}>
              <div style={{ fontSize: 14, color: '#0B1F2D', lineHeight: 2 }}>
                <div><strong>Email:</strong> <a href="mailto:info@esgine.io" style={{ color: '#1B6F5E' }}>info@esgine.io</a></div>
                <div><strong>United States:</strong> 20 F Street NW, #700, Washington, DC 20001</div>
                <div><strong>United Kingdom:</strong> Peter House, Oxford Street, #408, Manchester, M1 5AN</div>
              </div>
            </div>
          </Section>

        </div>
      </div>

      <Partners />
      <Compliance />
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, color: '#0B1F2D', letterSpacing: '-0.02em', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E8E5DC' }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function P({ children, style }) {
  return <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)', ...style }}>{children}</p>;
}

function Li({ children }) {
  return <li style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(11,31,45,0.75)' }}>{children}</li>;
}

function Def({ term, def }) {
  return (
    <div style={{ padding: '12px 16px', borderRadius: 8, background: '#FAFAF5', border: '1px solid #E8E5DC' }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#0B1F2D' }}>{term}</span>
      <span style={{ fontSize: 14, color: 'rgba(11,31,45,0.7)' }}> — {def}</span>
    </div>
  );
}
